import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/endpoints/auth.api';
import type { User, LoginCredentials, UserRole, Permission } from '@/types/user.types';
import { UserRoles } from '@/types/user.types';
import { getAccessToken, setAuthTokens, clearAuthTokens } from '@/lib/secureStorage';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: Permission | string) => boolean;
  hasAnyPermission: (permissions: (Permission | string)[]) => boolean;
  hasRole: (role: UserRole | string) => boolean;
  hasAnyRole: (roles: (UserRole | string)[]) => boolean;
  isAdmin: boolean;
  isDoctor: boolean;
  isNurse: boolean;
  isClinicalStaff: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = getAccessToken();
      if (token) {
        const userData = await authApi.getCurrentUser();
        setUser(userData);
      }
    } catch {
      clearAuthTokens();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials);
    setAuthTokens(response.accessToken, response.refreshToken);
    setUser(response.user);
    navigate('/dashboard');
  };

  const logout = () => {
    clearAuthTokens();
    setUser(null);
    navigate('/login');
  };

  const hasPermission = (permission: Permission | string): boolean => {
    if (!user) return false;
    // Admin has all permissions
    if (user.roles.includes(UserRoles.ADMIN)) return true;
    return user.permissions.includes(permission as Permission) || user.permissions.includes('*' as Permission);
  };

  const hasAnyPermission = (permissions: (Permission | string)[]): boolean => {
    return permissions.some((p) => hasPermission(p));
  };

  const hasRole = (role: UserRole | string): boolean => {
    if (!user) return false;
    // Admin has all roles implicitly
    if (user.roles.includes(UserRoles.ADMIN)) return true;
    return user.roles.includes(role as UserRole);
  };

  const hasAnyRole = (roles: (UserRole | string)[]): boolean => {
    return roles.some((r) => hasRole(r));
  };

  // Computed role checks
  const isAdmin = useMemo(() => user?.roles.includes(UserRoles.ADMIN) ?? false, [user]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isDoctor = useMemo(() => hasRole(UserRoles.DOCTOR), [user]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isNurse = useMemo(() => hasRole(UserRoles.NURSE), [user]);

   
  const isClinicalStaff = useMemo(
    () =>
      hasAnyRole([
        UserRoles.DOCTOR,
        UserRoles.NURSE,
        UserRoles.PSYCHOLOGIST,
        UserRoles.NUTRITIONIST,
        UserRoles.SOCIAL_WORKER,
      ]),
    [user]
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        hasPermission,
        hasAnyPermission,
        hasRole,
        hasAnyRole,
        isAdmin,
        isDoctor,
        isNurse,
        isClinicalStaff,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
